import React from "react";
import { Link } from "react-router-dom";
import { BrandMark } from "@/components/AppShell";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import { ArrowRight, ShieldCheck, Sparkles, Compass, Activity, Building2, Users, BadgeCheck, LineChart, FlaskConical, Rocket, Dna, ExternalLink, Stethoscope } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LanguageContext";
import { CARE_NET_LOGO } from "@/lib/careNet";
import CareNetLink from "@/components/CareNetLink";

const MODULES = [
  { icon: ShieldCheck, title: "Healthcare Trust Index", desc: "Verified benchmarking across 11 objective criteria — from Clinical Quality to ESG." },
  { icon: Activity, title: "Patient Advocacy", desc: "Symptom triage, provider discovery, records, reminders — a companion for every care journey." },
  { icon: BadgeCheck, title: "Doctor Excellence", desc: "Verified credentials, practice analytics, CME, and reputation intelligence." },
  { icon: Building2, title: "Hospital Command", desc: "Executive dashboards for Trust Index, incidents, waiting times, accreditation, and ESG." },
  { icon: LineChart, title: "AI Assessment Engine", desc: "Objective, rule-based scoring today — evolving toward AI orchestration tomorrow." },
  { icon: FlaskConical, title: "CRO Insights", desc: "20-layer Clinical Research Intelligence Stack — trial portfolios, PI scorecards, vendor oversight, regulatory submissions." },
  { icon: Dna, title: "GenomePramana", desc: "Precision-prevention loop: biomarkers + lifestyle (optional genome) → top-5 ranked health opportunities with evidence-graded interventions and outcome tracking." },
  { icon: Rocket, title: "Change Makers · Startups", desc: "Verified changemakers, healthcare founders and startup registry — the innovation layer of the ecosystem." },
  { icon: Users, title: "15 Stakeholder Portals", desc: "One ecosystem: patients, providers, payers, government, associations — all interoperable." },
];

const STATS = [
  { value: "11", label: "Trust criteria" },
  { value: "4", label: "Live portals" },
  { value: "9", label: "Modules" },
  { value: "100M+", label: "Users target" },
];

export default function Landing() {
  const { user } = useAuth();
  const { t } = useLang();

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-white/75 border-b border-border/60">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-4 flex items-center justify-between">
          <Link to="/" data-testid="landing-brand"><BrandMark /></Link>
          <nav className="hidden md:flex items-center gap-8 text-sm text-muted-foreground">
            <a href="#modules" className="hover:text-primary transition-colors">{t("nav.modules", "Modules")}</a>
            <a href="#trust" className="hover:text-primary transition-colors">{t("nav.trust_index", "Trust Index")}</a>
            <Link to="/directory" className="hover:text-primary transition-colors" data-testid="nav-directory">{t("nav.directory", "Directory")}</Link>
            <Link to="/network" className="hover:text-primary transition-colors" data-testid="nav-network">{t("nav.network", "Network")}</Link>
            <Link to="/startups" className="hover:text-primary transition-colors" data-testid="nav-landing-startups">{t("nav.startups", "Startups")}</Link>
            <CareNetLink
              className="hover:text-primary transition-colors inline-flex items-center gap-1.5"
              data-testid="nav-carenet"
            >
              <img src={CARE_NET_LOGO} alt="" className="h-4 w-4 object-contain" aria-hidden="true" />
              {t("nav.carenet", "Care Net")}
              <ExternalLink size={10} className="opacity-60" />
            </CareNetLink>
          </nav>
          <div className="flex items-center gap-2">
            <LanguageSwitcher variant="expanded" />
            {user ? (
              <Link to={roleHome(user.role)} data-testid="nav-dashboard">
                <Button className="rounded-md">
                  Go to Dashboard <ArrowRight size={14} className="ml-1.5" />
                </Button>
              </Link>
            ) : (
              <>
                <Link to="/login" data-testid="nav-login">
                  <Button variant="ghost" className="rounded-md hover:bg-primary/5">{t("cta.sign_in", "Sign in")}</Button>
                </Link>
                <Link to="/register" data-testid="nav-register">
                  <Button className="rounded-md">
                    {t("cta.get_started", "Get started")} <ArrowRight size={14} className="ml-1.5" />
                  </Button>
                </Link>
              </>
            )}
          </div>
        </div>
      </header>

      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-grid opacity-60 pointer-events-none" />
        <div className="max-w-7xl mx-auto px-6 lg:px-10 pt-20 pb-24 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center relative">
          <div className="lg:col-span-7 reveal">
            <div className="inline-flex items-center gap-2 rounded-full border border-border bg-white/70 px-3 py-1 text-xs text-muted-foreground">
              <Sparkles size={12} className="text-accent" />
              <span>{t("landing.hero.eyebrow", "Health Trust Infrastructure")}</span>
            </div>
            <h1 className="mt-6 font-serif text-5xl md:text-7xl leading-[0.95] tracking-tight text-primary">
              {t("landing.hero.title.a", "Building trust.")}<br />
              <span className="italic text-accent">{t("landing.hero.title.b", "Verifying care.").replace(/\.$/, "")}</span>.
            </h1>
            <p className="mt-6 text-lg md:text-xl text-muted-foreground max-w-xl leading-relaxed">
              {t("landing.hero.subtitle", "Arogya Pramana turns healthcare quality into a measurable, verifiable asset — for patients, doctors, hospitals, and the nation.")}
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-3">
              <Link to={user ? roleHome(user.role) : "/register"} data-testid="hero-primary-cta">
                <Button size="lg" className="rounded-md h-12 px-6">
                  {user ? "Open dashboard" : t("cta.create_account", "Create your account")}
                  <ArrowRight size={16} className="ml-2" />
                </Button>
              </Link>
              <Link to="/directory" data-testid="hero-secondary-cta">
                <Button size="lg" variant="outline" className="rounded-md h-12 px-6 border-primary text-primary hover:bg-primary/5">
                  {t("cta.browse_directory", "Browse verified providers")}
                </Button>
              </Link>
            </div>

            <div className="mt-10 grid grid-cols-4 gap-6 max-w-md">
              {STATS.map((s) => (
                <div key={s.label}>
                  <div className="text-2xl md:text-3xl font-semibold text-primary">{s.value}</div>
                  <div className="text-[11px] uppercase tracking-wider text-muted-foreground mt-0.5">{s.label}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="lg:col-span-5 reveal reveal-delay-2">
            <div className="rounded-xl border border-border bg-white p-6 md:p-7 shadow-sm">
              <div className="flex items-center justify-between">
                <div>
                  <div className="overline">Hospital Command · live</div>
                  <div className="font-serif text-2xl text-primary mt-1">Kokilaben Hospital</div>
                </div>
                <span className="inline-flex items-center gap-1.5 rounded-full border border-[#64748B]/25 bg-[#64748B]/10 text-[#334155] text-[10px] font-semibold uppercase tracking-wider px-2.5 py-1">
                  <span className="w-1.5 h-1.5 rounded-full bg-current" /> Platinum · 93
                </span>
              </div>

              <div className="mt-6 flex items-baseline gap-3">
                <div className="font-serif text-7xl text-primary tracking-tight leading-none">93</div>
                <div className="text-sm text-muted-foreground pb-2">Trust Index · percentile</div>
              </div>

              <div className="mt-6 grid grid-cols-3 gap-3 text-sm">
                <div className="rounded-md bg-muted p-3">
                  <div className="overline text-[10px]">Waiting</div>
                  <div className="font-serif text-xl text-primary mt-0.5">18m</div>
                </div>
                <div className="rounded-md bg-primary text-primary-foreground p-3">
                  <div className="overline text-[10px] text-primary-foreground/80">Beds</div>
                  <div className="font-serif text-xl mt-0.5">74%</div>
                </div>
                <div className="rounded-md bg-accent text-accent-foreground p-3">
                  <div className="overline text-[10px] text-accent-foreground/80">Incidents</div>
                  <div className="font-serif text-xl mt-0.5">3</div>
                </div>
              </div>

              <div className="mt-6">
                <div className="flex items-center justify-between text-xs">
                  <span className="overline">Patient satisfaction · 7d</span>
                  <span className="text-primary font-medium">82% ↑</span>
                </div>
                <div className="mt-3 grid grid-cols-7 gap-1.5">
                  {[42, 58, 63, 70, 68, 76, 82].map((h, i) => (
                    <div key={i} className="h-14 bg-muted rounded-sm overflow-hidden flex items-end">
                      <div className="w-full bg-primary rounded-sm transition-all" style={{ height: `${h}%` }} />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Positioning strip */}
      <section className="border-y border-border bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-6 flex flex-col md:flex-row md:items-center md:justify-between gap-4 text-sm">
          <div className="text-muted-foreground">
            Not a review site. A <span className="text-primary font-semibold">verification infrastructure</span> for Indian healthcare.
          </div>
          <div className="flex items-center gap-6 text-muted-foreground">
            <span>NABH-aligned</span>
            <span>·</span>
            <span>DPDP-ready</span>
            <span>·</span>
            <span>HL7/FHIR compatible</span>
          </div>
        </div>
      </section>

      {/* Modules */}
      <section id="modules" className="max-w-7xl mx-auto px-6 lg:px-10 py-24">
        <div className="max-w-2xl">
          <div className="overline">One ecosystem</div>
          <h2 className="mt-3 font-serif text-4xl md:text-5xl text-primary leading-tight">
            Nine modules. One coherent trust fabric.
          </h2>
          <p className="mt-4 text-muted-foreground">
            Each module speaks the same language of verified metrics — so patients, providers, and payers finally see the same picture.
          </p>
        </div>
        <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {MODULES.map((m, i) => (
            <div
              key={m.title}
              className="group rounded-lg border border-border bg-white p-6 hover:border-primary transition-colors reveal"
              style={{ animationDelay: `${i * 60}ms` }}
              data-testid={`module-card-${i}`}
            >
              <div className="w-10 h-10 rounded-md bg-primary/8 text-primary flex items-center justify-center group-hover:bg-primary group-hover:text-primary-foreground transition-colors">
                <m.icon size={18} />
              </div>
              <h3 className="mt-5 font-serif text-2xl text-primary">{m.title}</h3>
              <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{m.desc}</p>
            </div>
          ))}
        </div>

        {/* ClinicGrowth · CareOS module card — doctor-facing growth stack */}
        <Link
          to="/clinic-growth"
          className="mt-6 group block rounded-xl border border-sky-200 bg-gradient-to-br from-sky-50 via-white to-sky-50/40 hover:shadow-lg hover:-translate-y-0.5 transition-all overflow-hidden"
          data-testid="module-clinic-growth-card"
        >
          <div className="grid md:grid-cols-5 gap-6 p-6 md:p-8 items-center">
            <div className="md:col-span-1 flex items-center justify-center">
              <div className="h-28 w-28 rounded-2xl bg-white border border-sky-100 flex items-center justify-center shadow-sm overflow-hidden">
                <img src="/assets/clinic-growth-logo.webp" alt="ClinicGrowth & CareOS" className="h-24 w-24 object-contain" />
              </div>
            </div>
            <div className="md:col-span-4">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="overline text-sky-800">Doctor growth stack</span>
                <span className="rounded-full border border-sky-200 bg-sky-50 px-2 py-0.5 text-[10px] font-semibold text-sky-800">Live · MVP</span>
              </div>
              <h3 className="mt-2 font-serif text-3xl text-primary">ClinicGrowth · CareOS</h3>
              <p className="mt-2 text-base text-muted-foreground max-w-2xl">
                Hyper-local Meta campaigns with strict radius locks, AI symptom
                triage under Telemedicine Guidelines, and QR-verifiable
                e-prescriptions dispatched over WhatsApp — the full acquisition-to-care
                loop for Indian doctors.
              </p>
              <div className="mt-4 flex items-center gap-2 text-primary group-hover:translate-x-1 transition-transform text-sm font-medium">
                <Stethoscope size={14} />
                <span>Open ClinicGrowth</span>
                <ArrowRight size={14} />
              </div>
            </div>
          </div>
        </Link>

        {/* Care Net — sister network preview */}
        <CareNetLink
          className="mt-6 group block rounded-xl border border-primary/15 bg-gradient-to-br from-[#F3FBF8] via-white to-[#F2F0FE] hover:shadow-lg hover:-translate-y-0.5 transition-all overflow-hidden"
          data-testid="module-carenet-card"
        >
          <div className="grid md:grid-cols-5 gap-6 p-6 md:p-8 items-center">
            <div className="md:col-span-1 flex items-center justify-center">
              <div className="h-28 w-28 rounded-2xl bg-white border border-primary/10 flex items-center justify-center shadow-sm">
                <img src={CARE_NET_LOGO} alt="Care Net" className="h-20 w-20 object-contain" />
              </div>
            </div>
            <div className="md:col-span-4">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="overline text-emerald-700">Sister network</span>
                <span className="rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-[10px] font-semibold text-emerald-800">Live</span>
              </div>
              <h3 className="mt-2 font-serif text-3xl text-primary">Care Net</h3>
              <p className="mt-2 text-base text-muted-foreground max-w-2xl">
                A trusted peer-approval network for Indian doctors — endorse colleagues, verify practice, and earn a visible professional vouch that follows you across Arogya Pramana.
              </p>
              <div className="mt-4 flex items-center gap-2 text-primary group-hover:translate-x-1 transition-transform text-sm font-medium">
                <span>Open Care Net</span>
                <ExternalLink size={14} />
              </div>
            </div>
          </div>
        </CareNetLink>
      </section>

      {/* Dashboards as cards */}
      <section id="dashboards" className="border-y border-border bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-24">
          <div className="max-w-2xl">
            <div className="overline">Every stakeholder, one platform</div>
            <h2 className="mt-3 font-serif text-4xl md:text-5xl text-primary leading-tight">
              Dashboards designed for the role, not the tool.
            </h2>
            <p className="mt-4 text-muted-foreground">
              Sign in and land exactly where the data matters most — advocacy for patients, reputation for doctors, command for hospitals, oversight for admins.
            </p>
          </div>

          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-5">
            <Link to="/login" data-testid="dashboard-card-patient" className="group rounded-xl border border-pastel-mint-border bg-pastel-mint-bg p-6 hover:shadow-md hover:-translate-y-0.5 transition-all">
              <div className="flex items-start justify-between">
                <div>
                  <div className="overline text-pastel-mint-fg/80">Patient portal</div>
                  <div className="font-serif text-2xl text-pastel-mint-fg mt-1">Personal advocacy</div>
                </div>
                <Activity size={18} className="text-pastel-mint-fg opacity-80 group-hover:opacity-100" />
              </div>
              <div className="mt-6 grid grid-cols-3 gap-2 text-xs">
                <MiniStat label="Upcoming" value="3" />
                <MiniStat label="Records" value="12" />
                <MiniStat label="Reminders" value="2" tone="accent" />
              </div>
              <div className="mt-4 text-sm text-pastel-mint-fg/85">Symptom triage · records · appointments</div>
              <div className="mt-3 text-pastel-mint-fg text-xs font-medium inline-flex items-center gap-1 group-hover:gap-2 transition-all">Preview <ArrowRight size={12} /></div>
            </Link>

            <Link to="/login" data-testid="dashboard-card-doctor" className="group rounded-xl border border-pastel-sky-border bg-pastel-sky-bg p-6 hover:shadow-md hover:-translate-y-0.5 transition-all">
              <div className="flex items-start justify-between">
                <div>
                  <div className="overline text-pastel-sky-fg/80">Doctor portal</div>
                  <div className="font-serif text-2xl text-pastel-sky-fg mt-1">Verified reputation</div>
                </div>
                <BadgeCheck size={18} className="text-pastel-sky-fg opacity-80 group-hover:opacity-100" />
              </div>
              <div className="mt-6 flex items-end gap-6">
                <div>
                  <div className="overline text-[9px] text-pastel-sky-fg/70">Trust Index</div>
                  <div className="font-serif text-4xl text-pastel-sky-fg leading-none mt-1">92</div>
                </div>
                <div className="flex-1 grid grid-cols-8 gap-1 h-12">
                  {[40, 55, 62, 58, 70, 74, 80, 88].map((h, i) => (
                    <div key={i} className="h-full bg-white/40 rounded-sm flex items-end">
                      <div className="w-full bg-pastel-sky-fg rounded-sm" style={{ height: `${h}%` }} />
                    </div>
                  ))}
                </div>
              </div>
              <div className="mt-4 text-sm text-pastel-sky-fg/85">Practice analytics · CME · appointments</div>
              <div className="mt-3 text-pastel-sky-fg text-xs font-medium inline-flex items-center gap-1 group-hover:gap-2 transition-all">Preview <ArrowRight size={12} /></div>
            </Link>

            <Link to="/login" data-testid="dashboard-card-hospital" className="group rounded-xl border border-pastel-lavender-border bg-pastel-lavender-bg p-6 hover:shadow-md hover:-translate-y-0.5 transition-all">
              <div className="flex items-start justify-between">
                <div>
                  <div className="overline text-pastel-lavender-fg/80">Hospital command</div>
                  <div className="font-serif text-2xl text-pastel-lavender-fg mt-1">Executive dashboard</div>
                </div>
                <Building2 size={18} className="text-pastel-lavender-fg opacity-80 group-hover:opacity-100" />
              </div>
              <div className="mt-6 grid grid-cols-3 gap-2 text-xs">
                <div className="rounded-md bg-white/50 p-3">
                  <div className="overline text-[10px] text-pastel-lavender-fg/70">Wait</div>
                  <div className="font-serif text-xl mt-0.5 text-pastel-lavender-fg">18m</div>
                </div>
                <div className="rounded-md bg-white/50 p-3">
                  <div className="overline text-[10px] text-pastel-lavender-fg/70">Beds</div>
                  <div className="font-serif text-xl mt-0.5 text-pastel-lavender-fg">74%</div>
                </div>
                <div className="rounded-md bg-white/50 p-3">
                  <div className="overline text-[10px] text-pastel-lavender-fg/70">Incidents</div>
                  <div className="font-serif text-xl mt-0.5 text-pastel-lavender-fg">3</div>
                </div>
              </div>
              <div className="mt-4 text-sm text-pastel-lavender-fg/85">KPIs · Trust Index · incidents · ESG</div>
              <div className="mt-3 text-pastel-lavender-fg text-xs font-medium inline-flex items-center gap-1 group-hover:gap-2 transition-all">Preview <ArrowRight size={12} /></div>
            </Link>

            <Link to="/login" data-testid="dashboard-card-admin" className="group rounded-xl border border-pastel-peach-border bg-pastel-peach-bg p-6 hover:shadow-md hover:-translate-y-0.5 transition-all">
              <div className="flex items-start justify-between">
                <div>
                  <div className="overline text-pastel-peach-fg/80">Platform admin</div>
                  <div className="font-serif text-2xl text-pastel-peach-fg mt-1">National oversight</div>
                </div>
                <ShieldCheck size={18} className="text-pastel-peach-fg opacity-80 group-hover:opacity-100" />
              </div>
              <div className="mt-6 grid grid-cols-4 gap-2 text-xs">
                <MiniStat label="Users" value="1.2k" />
                <MiniStat label="Docs" value="12" />
                <MiniStat label="Hosp." value="8" />
                <MiniStat label="Alerts" value="3" tone="accent" />
              </div>
              <div className="mt-4 text-sm text-pastel-peach-fg/85">Users · verifications · system stats</div>
              <div className="mt-3 text-pastel-peach-fg text-xs font-medium inline-flex items-center gap-1 group-hover:gap-2 transition-all">Preview <ArrowRight size={12} /></div>
            </Link>
          </div>
        </div>
      </section>

      {/* Trust Index */}
      <section id="trust" className="bg-white border-y border-border">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-24 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-5">
            <div className="overline">Trust Index</div>
            <h2 className="mt-3 font-serif text-4xl md:text-5xl text-primary leading-tight">
              A percentile score, not a star rating.
            </h2>
            <p className="mt-5 text-muted-foreground leading-relaxed">
              Every doctor and hospital is scored across 11 objective criteria — Clinical Quality,
              Patient Experience, Digital Maturity, AI Readiness, Safety, Governance,
              CME, Accessibility, Research, Ethics, and Sustainability.
            </p>
            <div className="mt-6 flex flex-wrap gap-2">
              {["Bronze", "Silver", "Gold", "Platinum"].map((t, i) => (
                <span
                  key={t}
                  className="text-xs font-semibold uppercase tracking-wider rounded-full border px-3 py-1"
                  style={{
                    background: ["#C583611F", "#9CA3AF1F", "#EAB3081F", "#64748B1F"][i],
                    color: ["#8B4513", "#475569", "#854D0E", "#334155"][i],
                    borderColor: ["#C5836140", "#9CA3AF40", "#EAB30840", "#64748B40"][i],
                  }}
                >
                  {t}
                </span>
              ))}
            </div>
            <Link to="/directory" className="inline-flex items-center gap-1.5 mt-8 text-primary font-medium hover:gap-3 transition-all" data-testid="trust-cta">
              Explore the directory <ArrowRight size={14} />
            </Link>
          </div>
          <div className="lg:col-span-7">
            <div className="rounded-lg border border-border p-4 bg-background">
              <div className="grid grid-cols-11 gap-1 text-[10px] text-muted-foreground text-center mb-2">
                {["Clinical", "Experience", "Digital", "AI", "Safety", "Gov.", "CME", "Access", "Research", "Ethics", "ESG"].map((n) => (
                  <div key={n}>{n}</div>
                ))}
              </div>
              <div className="grid grid-cols-11 gap-1">
                {[92, 88, 78, 65, 91, 84, 72, 80, 68, 95, 74].map((v, i) => (
                  <div key={i} className="h-32 bg-muted rounded-sm flex items-end">
                    <div className="w-full bg-primary rounded-sm transition-all duration-500" style={{ height: `${v}%` }} />
                  </div>
                ))}
              </div>
              <div className="mt-4 flex items-center justify-between text-xs">
                <span className="text-muted-foreground">Fortis Escorts Heart Institute · Delhi</span>
                <span className="inline-flex items-center gap-1 rounded-full border border-[#EAB308]/30 bg-[#EAB308]/12 text-[#854D0E] font-semibold uppercase tracking-wider px-2 py-0.5">
                  Gold · 87
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-24">
        <div className="rounded-lg bg-primary text-primary-foreground p-10 md:p-14 relative overflow-hidden">
          <div className="absolute inset-0 bg-dots opacity-30" />
          <div className="relative grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
            <div>
              <div className="overline text-primary-foreground/70">Get involved</div>
              <h2 className="mt-3 font-serif text-4xl md:text-5xl leading-tight">
                Verify your practice.<br /> Advocate for your patients.
              </h2>
            </div>
            <div className="md:justify-self-end flex flex-col sm:flex-row gap-3">
              <Link to="/register" data-testid="cta-register">
                <Button size="lg" variant="secondary" className="rounded-md h-12 px-6 bg-white text-primary hover:bg-white/90">
                  Start free
                </Button>
              </Link>
              <Link to="/login" data-testid="cta-signin">
                <Button size="lg" variant="ghost" className="rounded-md h-12 px-6 text-primary-foreground border border-primary-foreground/40 hover:bg-primary-foreground/10 hover:text-primary-foreground">
                  Sign in
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      <footer className="border-t border-border bg-white" data-testid="footer">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-10 grid grid-cols-1 md:grid-cols-4 gap-8">
          <div className="md:col-span-2">
            <BrandMark />
            <p className="mt-3 text-xs text-muted-foreground max-w-sm">
              A Data Fiduciary under India&apos;s Digital Personal Data Protection Act, 2023.
              Arogya Pramana Trust Infrastructure Pvt. Ltd. · BKC, Mumbai.
            </p>
          </div>
          <div>
            <div className="overline mb-3">Product</div>
            <ul className="space-y-1.5 text-sm text-muted-foreground">
              <li><a href="#modules" className="hover:text-primary">Modules</a></li>
              <li><a href="#dashboards" className="hover:text-primary">Dashboards</a></li>
              <li><a href="#trust" className="hover:text-primary">Trust Index</a></li>
              <li><Link to="/directory" className="hover:text-primary">Directory</Link></li>
              <li><Link to="/network" className="hover:text-primary">Network</Link></li>
              <li><Link to="/startups" className="hover:text-primary">Startups</Link></li>
            </ul>
          </div>
          <div>
            <div className="overline mb-3">DPDP & Legal</div>
            <ul className="space-y-1.5 text-sm text-muted-foreground">
              <li><Link to="/dpdp" className="hover:text-primary" data-testid="footer-dpdp">DPDP rights</Link></li>
              <li><Link to="/privacy" className="hover:text-primary" data-testid="footer-privacy">Privacy Policy</Link></li>
              <li><Link to="/terms" className="hover:text-primary" data-testid="footer-terms">Terms of Service</Link></li>
              <li><Link to="/grievance" className="hover:text-primary" data-testid="footer-grievance">File a grievance</Link></li>
              <li><a href="mailto:dpo@arogyapramana.in" className="hover:text-primary">dpo@arogyapramana.in</a></li>
            </ul>
          </div>
        </div>
        <div className="border-t border-border">
          <div className="max-w-7xl mx-auto px-6 lg:px-10 py-5 flex flex-col md:flex-row md:items-center md:justify-between gap-2 text-xs text-muted-foreground">
            <div>© 2026 Arogya Pramana. Building trust. Improving care.</div>
            <div>DPO: Dr. Ananya Rao · Grievance SLA 30 days · Policy v2026-02-01</div>
          </div>
        </div>
      </footer>
    </div>
  );
}

function roleHome(role) {
  switch (role) {
    case "doctor": return "/doctor";
    case "hospital": return "/hospital";
    case "admin": return "/admin";
    default: return "/patient";
  }
}

function MiniStat({ label, value, tone }) {
  const accent = tone === "accent";
  return (
    <div className={`rounded-md p-3 border ${accent ? "border-accent/25 bg-accent/10" : "border-border bg-white"}`}>
      <div className={`overline text-[9px] ${accent ? "text-accent" : ""}`}>{label}</div>
      <div className={`font-serif text-xl mt-0.5 ${accent ? "text-accent" : "text-primary"}`}>{value}</div>
    </div>
  );
}
